import {FoodEntry, Nutrient, NutrientGroup, PortionSize} from "../domain/FoodEntry";
import {getFoodCacheEntry} from "./FoodSearch";
import React, { type JSX } from "react";
import type {RecipeIngredient} from "../domain/RcpRecipe";

export class CalculatedItem {
    nutrient: Nutrient;
    value: number;
    hasValue: boolean;

    constructor(nutrient: Nutrient) {
        this.nutrient = nutrient;
        this.value = 0;
        this.hasValue = false;
    }
}

export class CalculatedItemSet {
    totalWeightGrams: number;
    nutrients: CalculatedItem[];

    constructor() {
        this.totalWeightGrams = 0;
        this.nutrients = [];
    }

    /**
     * Add all the nutrients for a given food entry given a particular portion size and quantity.
     * @param foodEntry {FoodEntry} the food entry to add nutrients for
     * @param quantity {number} the quantity of the below portions
     * @param portionSize {PortionSize} the portion size to use, must be populated (see FoodEntry.portionFor)
     */
    addFoodEntry(foodEntry: FoodEntry, quantity: number, portionSize: PortionSize) {
        if(this.nutrients.length === 0) {
            this.nutrients = [...Nutrient.ENUM_VALUES]
                .sort((a, b) => a.ordinal < b.ordinal ? -1 : 1)
                .map(n => new CalculatedItem(n));
        }
        let itemWeight = quantity * portionSize.gramQuantity;
        this.totalWeightGrams += itemWeight;
        foodEntry.nutrients.forEach(fn => {
            // find nutrient in our list (hopefully always there)
            let item = this.nutrients.find(n => n.nutrient === fn.nutrient);
            if(item) {
                item.hasValue = true;
                item.value += fn.nutrient.defMeasure.fromSourceUnit((fn.value/100.0) * itemWeight, fn.measurement);
            }
        });
    }
}

/**
 * Converts a list of food entries into a single food entry
 * @param listFoodEntries {[RecipeIngredient]} the list of input food entries
 * @return {CalculatedItemSet} providing the total weight and nutrient values
 */
export function convertAllFoodEntryIntoSingleEntry(listFoodEntries: RecipeIngredient[]): CalculatedItemSet {
    const result = new CalculatedItemSet();

    listFoodEntries.forEach(ri => {
        if (ri && ri.foodEntryId !== -1) {
            let foodEntry = getFoodCacheEntry(ri.foodEntryId);
            if (foodEntry) {
                result.addFoodEntry(foodEntry, ri.quantity, foodEntry.portionFor(ri.portionSizeId));
            }
        }
    });

    return result;
}

export function mapGroupToClazz(group: NutrientGroup) {
    if(group === NutrientGroup.FATS) return "nutrient-fats";
    else if(group === NutrientGroup.CARB_ENERGY) return "nutrient-energy";
    else return "nutrient-vitamins";
}

/**
 * This is a core food entry viewer that presents all the nutrients in the well known table format.
 * @param itemSet {CalculatedItemSet} the item set to be presented
 * @param servings {number} the number of servings
 * @returns {JSX.Element}
 * @constructor
 */
export function ItemSetFoodEntryViewer({ itemSet, servings }: { itemSet: CalculatedItemSet, servings: number }): JSX.Element {
    if(!itemSet) return <div>No item set provided</div>;
    return <div><table className="nutrientTable">
        <thead>
        <tr><th colSpan={3}>Nutritional Information</th></tr>
        <tr>
            <th>Nutrient</th>
            <th>Servings ({servings})</th>
            <th>Per 100g</th>
        </tr>
        </thead>
        <tbody>
        {itemSet.nutrients.filter(ci => ci.hasValue).map(ci =>
            <tr className={mapGroupToClazz(ci.nutrient.group)}>
                <td>{ci.nutrient.name}</td>
                <td>{(ci.value / servings).toFixed(2)} {ci.nutrient.defMeasure.unit}</td>
                <td>{(ci.value /(itemSet.totalWeightGrams/100)).toFixed(2)} {ci.nutrient.defMeasure.unit}</td>
            </tr>
        )}
        </tbody>
    </table></div>;
}